/**
 * Single source of truth for member- and coach-facing appointment status copy.
 *
 * MEN-4996: these surfaces used to inline their own strings, which drifted into
 * exposing operation UUIDs and internal vocabulary ("retained key", "replayed")
 * in states that are a delay, not a failure. Platform-admin copy is deliberately
 * NOT owned here — Ops needs the operation handle and keeps its own constants in
 * usePlatformAdminAppointmentLifecycleGuard.ts.
 */

export type AppointmentActionKind = 'booking' | 'reschedule' | 'cancellation';

const PENDING_SUBJECT: Record<AppointmentActionKind, string> = {
  booking: 'this time',
  cancellation: 'this cancellation',
  reschedule: 'the new time',
};

export const appointmentPendingMessage = (
  kind: AppointmentActionKind,
): string =>
  `We are confirming ${PENDING_SUBJECT[kind]} with Google. Please wait a moment.`;

const RESTORED_SUBJECT: Record<AppointmentActionKind, string> = {
  booking: 'booking',
  cancellation: 'cancellation',
  reschedule: 'time change',
};

/**
 * Copy for a submit that was interrupted by a reload.
 *
 * `restoredSubmitting` means only that a request was in flight when the page
 * reloaded. It does NOT guarantee that the client is polling for the outcome —
 * reconciliation returns early when the retained command cannot be decoded
 * (MemberRescheduleSession.tsx:793-799) — and it does not guarantee that a retry
 * control is rendered, because on the member reschedule surface that button
 * lives inside `timesContent` and needs a selected slot. So this message never
 * claims we are checking, and it points at the retry button only when the caller
 * confirms one is actually on screen.
 */
export const appointmentRestoredSubmittingMessage = (
  kind: AppointmentActionKind,
  { retryAvailable }: { retryAvailable: boolean },
): string =>
  `Your last ${RESTORED_SUBJECT[kind]} was interrupted when the page reloaded, so we could not confirm whether it went through. Nothing was sent again.${
    retryAvailable
      ? ' Use the button below to try the same request once more.'
      : ' Check your upcoming sessions to see the current time before trying again.'
  }`;

export const APPOINTMENT_CONFIRMATION_PAUSED_MESSAGE =
  'Refresh the page after updating the app to finish confirming this booking.';

/** Stable hook so ownership tests can assert on the panel, not on a UUID. */
export const PENDING_APPOINTMENT_PANEL_TEST_ID = 'pending-appointment-panel';

export const APPOINTMENT_RETRY_BUTTON_LABEL = 'Try again';

/**
 * Member-safe copy for an upstream decline: a write the scheduling service
 * refused to admit.
 *
 * The reason is a machine token forwarded verbatim from Moments through the
 * backend's envelope (`calendar_conflict_check_unavailable`,
 * `recurring_coaching_create_disabled`, ...). It is NOT display text, and the
 * booking surfaces render an error envelope's `error` string directly, so a
 * token that reaches them appears on screen.
 *
 * The token is therefore translated once, inside the API layer, before the
 * transport error is handed to any component (appointmentQueryError in
 * store/api/appointmentApi.ts). What crosses that boundary is
 * `AppointmentDeclineCopy` — already-safe fragments — never the token. Unknown
 * reasons resolve to neutral copy rather than being shown, so a token added
 * upstream tomorrow cannot leak by default.
 *
 * Every branch states both facts the member needs: which check is unavailable,
 * and that nothing was changed. The second is the entire point of
 * `write_outcome: "not_applied"` and must survive into the copy.
 */
export interface AppointmentDeclineCopy {
  cause: string;
  guidance: string;
}

type DeclineCopy = AppointmentDeclineCopy;

const TRANSIENT_GUIDANCE = 'Please try again in a few minutes.';
// A deliberate gate, not an outage: it can stay off for a long time, so the
// copy must not promise a short wait.
const GATED_GUIDANCE = 'Please try again later.';

const DECLINE_COPY: Record<string, DeclineCopy> = {
  calendar_conflict_check_unavailable: {
    cause: 'We could not check calendar availability just now',
    guidance: TRANSIENT_GUIDANCE,
  },
  recurring_coaching_create_disabled: {
    cause: 'Recurring session booking is paused right now',
    guidance: GATED_GUIDANCE,
  },
  v3_one_off_coaching_create_disabled: {
    cause: 'Single session booking is paused right now',
    guidance: GATED_GUIDANCE,
  },
};

const DECLINE_FALLBACK: DeclineCopy = {
  cause: 'Scheduling is temporarily unavailable',
  guidance: TRANSIENT_GUIDANCE,
};

/**
 * `request` is the subject used when the API layer sanitizes the envelope: at
 * that point the action is known to be a declined appointment write, but not
 * which surface submitted it. It keeps the sentence true and member-safe for a
 * caller that renders the envelope verbatim.
 */
export type AppointmentDeclineSubject = AppointmentActionKind | 'request';

const DECLINE_SUBJECT: Record<AppointmentDeclineSubject, string> = {
  booking: 'this booking was not made',
  cancellation: 'this cancellation was not made',
  request: 'this request was not completed',
  reschedule: 'this time change was not made',
};

/** Token in, safe fragments out. The only place a reason token is read. */
export const appointmentDeclineCopy = (
  reason: string,
): AppointmentDeclineCopy => DECLINE_COPY[reason] ?? DECLINE_FALLBACK;

/** Safe fragments in, member sentence out. Never sees a reason token. */
export const appointmentDeclineMessage = (
  subject: AppointmentDeclineSubject,
  { cause, guidance }: AppointmentDeclineCopy,
): string =>
  `${cause}, so ${DECLINE_SUBJECT[subject]}. Nothing has changed. ${guidance}`;

export const appointmentDeclinedMessage = (
  subject: AppointmentDeclineSubject,
  reason: string,
): string => appointmentDeclineMessage(subject, appointmentDeclineCopy(reason));

export const APPOINTMENT_SLOT_UNAVAILABLE_MESSAGE =
  'That time is no longer available. Please choose another time.';

/**
 * The rejection code Moments returns for an operation that reached
 * needs_reconciliation. It is the only signal available on the member contract:
 * the v1 operation-status payload carries no reconciliation_reason field.
 */
export const APPOINTMENT_NEEDS_RECONCILIATION_CODE =
  'operation_needs_reconciliation';

/**
 * MEN-4915: copy for an operation whose recovery ladder ran out of attempts.
 *
 * Two things this copy must NOT say, both load-bearing.
 *
 * It must not say the session is booked. The reason this state is reachable at
 * all is a Google failure during recovery, and the recovery dispositions that
 * reach the cap disagree about what is on the calendar: an errored occurrence
 * patch leaves a live master event with attendees and a Meet link, a
 * BookRecoveryAbsent create failure leaves nothing, and a compensated terminal
 * conference has just deleted the master pending a recreate.
 *
 * It must not say the booking failed either, for the same reason -- in the
 * measured case (a 403 rateLimitExceeded patching occurrence descriptions) the
 * member does have a session. The slot fallback this replaces was worse still:
 * "That time is no longer available" is simply false here.
 *
 * So the copy reports the one thing that is true in every branch -- we could
 * not confirm it -- points at the place the member can see for themselves, and
 * steers them away from retrying into a duplicate. It deliberately does not
 * promise that anyone is already looking: nothing writes the
 * operator_review_required issue code today, so the Ops review queue does not
 * surface these.
 */
const TERMINAL_RECONCILIATION_MESSAGE: Record<AppointmentActionKind, string> = {
  booking:
    'We could not confirm this booking with Google. Check your upcoming sessions to see whether it is there, and contact support instead of booking again so you do not end up with two sessions.',
  cancellation:
    'We could not confirm this cancellation with Google. Check your upcoming sessions to see whether it is still booked, and contact support instead of trying again.',
  reschedule:
    'We could not confirm this time change with Google. Check your upcoming sessions to see which time is saved, and contact support instead of trying again.',
};

export const appointmentTerminalReconciliationMessage = (
  kind: AppointmentActionKind,
): string => TERMINAL_RECONCILIATION_MESSAGE[kind];

export interface AppointmentRejection {
  code?: string | null;
  message?: string | null;
}

/**
 * Maps an operation's command onto the copy kind.
 *
 * Sessions/Upcoming observes whichever operation it was handed off, so it
 * cannot hardcode a kind the way a single-purpose dialog can. Defaults to
 * booking, which is the only handoff that exists today; an operation arriving
 * without a command is treated as the booking it almost certainly is rather
 * than throwing on a surface whose whole job is to show the member something.
 */
export const appointmentActionKindForCommand = (
  command: string | null | undefined,
): AppointmentActionKind => {
  if (command === 'cancel') return 'cancellation';
  if (command === 'reschedule') return 'reschedule';
  return 'booking';
};

/**
 * Single translation point for an operation rejection shown to a member.
 *
 * Everything except the reconciliation code keeps its existing behaviour. The
 * fallback is a parameter because the surfaces do not share one: the booking
 * pages say the time is gone, while the member cancel and reschedule dialogs
 * say they could not complete the action. Replacing either with the other would
 * be a silent copy regression on a path that has nothing to do with this fix.
 */
export const appointmentRejectionMessage = (
  rejection: AppointmentRejection | null | undefined,
  kind: AppointmentActionKind,
  fallback: string = APPOINTMENT_SLOT_UNAVAILABLE_MESSAGE,
): string =>
  rejection?.code === APPOINTMENT_NEEDS_RECONCILIATION_CODE
    ? appointmentTerminalReconciliationMessage(kind)
    : rejection?.message || fallback;
